import { makeStyles, useTheme } from '@material-ui/styles'
import clsx from 'clsx'
import { prop } from 'ramda'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import {
  type CustomerTiers,
  dashboardUrl,
  logoutUrl,
  resetPasswordThroughEmailUrl,
} from 'app/constants'
import DocumentMeta from 'core/components/DocumentMeta'
import Progress from 'core/components/progress/Progress'
import {
  loadFeatures,
  loadLoginFeaturesAndTracking,
  validateSession,
} from 'core/containers/AppController'
import AuthContainer from 'core/containers/AuthContainer'
import PublicContainer from 'core/containers/PublicContainer'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import type Theme from 'core/themes/model'
import { isOpenCloud, showProdFrame as showProdFrameFn } from 'core/utils/helpers'
import { isNilOrEmpty } from 'utils/fp'
import { appCuesSetAnonymous, trackPage } from 'utils/tracking'

const useStyles = makeStyles((theme: Theme) => ({
  '@global': {
    html: {
      backgroundColor: theme?.components?.frame?.background,
    },
    'html body': {
      backgroundColor: theme.components?.frame?.background,
    },
    'html body #root': {
      transition: 'filter 300ms ease-out',
      filter: 'blur(0)',
      backgroundColor: theme?.components?.frame?.background,
    },
    'html body.blur #root': {
      transitionDelay: '200ms',
      filter: 'blur(1px)',
    },
    '*::-webkit-scrollbar': {
      borderRadius: 4,
      width: 14,
      height: 14,
    },

    '*.thin-scrollbar': {
      // FF supports these fields but not webkit
      scrollbarColor: `${theme?.components?.scrollbar?.thumb} ${theme?.components?.card?.background}`,
      scrollbarWidth: 'thin',
    },
    '*.thin-scrollbar::-webkit-scrollbar': {
      borderRadius: 4,
      width: 6,
      height: 6,
    },

    '*::-webkit-scrollbar-track': {
      '-webkit-box-shadow': `inset 0 0 200px ${theme?.components?.scrollbar?.track}`,
      borderRadius: 4,
    },

    '*::-webkit-scrollbar-thumb': {
      '-webkit-box-shadow': `inset 0 0 200px ${theme?.components?.scrollbar?.thumb}`,
      borderRadius: 14,

      // make the thumb look thinner than the track
      border: `3px solid ${theme?.components?.scrollbar?.track}`,
    },

    '*.thin-scrollbar::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      border: `1px solid ${theme?.components?.scrollbar?.track}`,
    },
  },
  root: {
    flexGrow: 1,
  },
  prodWarningBanner: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.palette.error.main,
    padding: theme.spacing(1),
    textAlign: 'center',
    zIndex: 9999,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: theme.palette.common.white,
  },
  warningIcon: {
    fontSize: '24px',
  },
  mainContentWithBanner: {
    paddingTop: '48px',
  },
  progress: {
    '& img': {
      height: 130,
    },
    '& img ~ span': {
      fontSize: theme.typography.subtitle1.fontSize,
    },
  },
}))

/**
 * Sets up the Openstack session.
 * Routes between PublicContainer (unauthenticated) and AuthContainer (authenticated).
 */
const AppContainer = () => {
  const classes = useStyles()
  const theme: Theme = useTheme()
  const { history } = useReactRouter()
  const [loginFeatures, setLoginFeatures] = useState<{
    customerTier: CustomerTiers
    ssoEnabled: boolean
  }>(null)
  const [sessionChecked, setSessionChecked] = useState(false)
  const { getUserPrefs } = useScopedPreferences()
  const session = useSelector(prop<string, SessionState>(sessionStoreKey))
  const { features, loadingTenant } = session

  // ⚠️ Do NOT refactor this to use `features?.sso_infra_url` directly.
  // validateSession destroys the session when the user is unauthenticated
  // so the features get set to its initial state (empty object).
  // We need the infraHost to be set before the session is validated
  const [infraHost, setInfraHost] = useState(null)
  const infraHostUrl = infraHost ? `https://${infraHost}` : undefined

  const frameColor = theme?.components?.frame?.background

  const metaThemeProps = useMemo(
    () => [
      {
        name: 'theme-color',
        content: frameColor,
      },

      {
        name: 'msapplication-navbutton-color',
        content: frameColor,
      },

      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black-translucent',
      },
    ],
    [frameColor],
  )

  // Load features and update session
  useEffect(() => {
    const getFeatures = async () => {
      const features = await loadFeatures()
      setInfraHost(features?.sso_infra_url)
    }
    getFeatures()
  }, [])

  useEffect(() => {
    const { pathname, hash } = history.location

    const unlisten = history.listen((location) => {
      trackPage(`${location.pathname}${location.hash}`)
      if (features?.experimental?.sandbox) {
        appCuesSetAnonymous()
      }
    })

    // This is to send page event for the first page the user lands on
    trackPage(`${pathname}${hash}`)
    if (features?.experimental?.sandbox) {
      appCuesSetAnonymous()
    }

    // Convert legacy hash URLs (e.g. /ui/index.html#/reset/password) to real paths
    // so BrowserRouter can match them to public routes
    // DO NOT REMOVE THIS - It causes this issue https://platform9.atlassian.net/browse/PCD-7843
    if (hash.includes(resetPasswordThroughEmailUrl)) {
      history.replace(hash.slice().replace('#', '/ui'))
    }

    const initialize = async () => {
      setLoginFeatures(await loadLoginFeaturesAndTracking())
      try {
        await validateSession(getUserPrefs, session, history)
      } catch (err) {
        console.error('Session validation failed during initialize:', err)
      } finally {
        // Always mark session as checked — an unhandled error must not leave
        // the app stuck in the loading state forever (e.g. a 401 on /domains
        // during Vite HMR when scopedToken is reset).
        setSessionChecked(true)
      }
    }
    initialize()

    return unlisten
  }, [])

  // return <Progress loading message={'Loading app'} />

  let from = history.location.pathname + history.location.search
  if (history?.location?.state?.from === logoutUrl) {
    from = dashboardUrl
  }

  const openCloudDataLoaded = !!infraHost
  const isPendingSsoLogout = history.location.search.includes('pendingSsoLogout=true')
  const isReady =
    sessionChecked &&
    !loadingTenant &&
    (!isOpenCloud() || openCloudDataLoaded) &&
    !isPendingSsoLogout

  // Determine if user is authenticated
  const isAuthenticated = !isNilOrEmpty(session) && !!session.username
  const showProdFrame = showProdFrameFn()

  return (
    <>
      {/* When in development mode and the domain is production, show a warning banner */}
      {showProdFrame && (
        <div className={classes.prodWarningBanner}>
          <span className={classes.warningIcon}>⚠️</span>
          <span>WARNING: You are accessing PRODUCTION/STAGING environment - Be careful!</span>
          <span className={classes.warningIcon}>⚠️</span>
        </div>
      )}
      <div
        className={clsx(classes.root, showProdFrame && classes.mainContentWithBanner)}
        id="_main-container"
      >
        <DocumentMeta meta={metaThemeProps} />
        {!isReady ? (
          <div className={classes.progress}>
            <Progress loading message={loadingTenant ? 'Loading tenant...' : 'Loading app'} />
          </div>
        ) : isAuthenticated ? (
          <AuthContainer />
        ) : (
          <PublicContainer loginFeatures={loginFeatures} infraHostUrl={infraHostUrl} />
        )}
      </div>
    </>
  )
}

export default AppContainer
